import React, { useState, useEffect } from "react";
import {
  FaRegCheckCircle,
  FaShieldAlt,
  FaDatabase,
  FaLock,
  FaCookieBite,
  FaLink,
  FaUserSecret,
  FaGlobe,
  FaHistory,
  FaEnvelope,
  FaExclamationTriangle,
  FaArrowRight
} from "react-icons/fa";
import Header from "../Home/Header";
import Footer from "../Home/Footer";
import "./PrivacyPolicy.css";

const sections = [
  { id: "collect", title: "1. Information We Collect", icon: <FaDatabase />, iconClass: "icon-blue" },
  { id: "use", title: "2. Use of Information", icon: <FaUserSecret />, iconClass: "icon-purple" },
  { id: "share", title: "3. Sharing of Information", icon: <FaLink />, iconClass: "icon-blue" },
  { id: "disclaimer", title: "4. Blockchain Disclaimer", icon: <FaExclamationTriangle />, iconClass: "icon-yellow" },
  { id: "cookies", title: "5. Cookies & Tracking", icon: <FaCookieBite />, iconClass: "icon-indigo" },
  { id: "security", title: "6. Data Security", icon: <FaShieldAlt />, iconClass: "icon-green" },
  { id: "retention", title: "7. Data Retention", icon: <FaHistory />, iconClass: "icon-purple" },
  { id: "rights", title: "8. Your Rights", icon: <FaRegCheckCircle />, iconClass: "icon-blue" },
  { id: "international", title: "9. International Transfers", icon: <FaGlobe />, iconClass: "icon-blue" },
  { id: "changes", title: "10. Changes to Policy", icon: <FaHistory />, iconClass: "icon-gray" },
  { id: "contact", title: "11. Contact Us", icon: <FaEnvelope />, iconClass: "icon-purple" },
];

const PrivacyPolicy = () => {
  const [activeSection, setActiveSection] = useState(sections[0].id);

  useEffect(() => {
    window.scrollTo(0, 0);

    const handleScroll = () => {
      let currentSection = sections[0].id;
      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element && window.scrollY >= element.offsetTop - 250) {
          currentSection = section.id;
        }
      }
      setActiveSection(currentSection);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      window.scrollTo({
        top: element.offsetTop - 120,
        behavior: "smooth"
      });
    }
  };

  return (
    <div className="privacy-page-container">
      <Header />

      {/* Hero Section */}
      <div className="privacy-hero">
        <div className="privacy-hero-bg-orb-1" />
        <div className="privacy-hero-bg-orb-2" />

        <div className="privacy-hero-content">
          <div className="privacy-effective-date">
            Effective Date: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </div>
          <h1 className="privacy-title">
            Privacy <span className="privacy-title-highlight">Policy</span>
          </h1>
          <p className="privacy-subtitle">
            Your data, our responsibility. Transparent practices for the Web3 ecosystem.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="privacy-container">

        {/* Sticky Sidebar Navigation */}
        <div className="privacy-sidebar">
          <div className="privacy-sidebar-nav">
            <h3 className="privacy-sidebar-title">Contents</h3>
            <div className="privacy-nav-list">
              {sections.map((sec) => (
                <button
                  key={sec.id}
                  onClick={() => scrollToSection(sec.id)}
                  className={`privacy-nav-btn ${activeSection === sec.id ? "active" : ""}`}
                >
                  <span className="privacy-nav-icon">{sec.icon}</span>
                  <span>{sec.title}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="privacy-main-content">

          {/* Section 1 */}
          <section id="collect" className="privacy-section-card">
            <div className="privacy-section-header">
              <div className={`privacy-icon-wrapper ${sections[0].iconClass}`}>
                {sections[0].icon}
              </div>
              <h2 className="privacy-section-title">1. Information We Collect</h2>
            </div>
            <p className="privacy-text">
              When you interact with Xeer Technology, we may collect different types of information to provide and improve our services:
            </p>
            <div className="privacy-grid">
              <div className="privacy-subcard">
                <h4>a. Personal Information</h4>
                <p>Name, email address, contact details provided via forms.</p>
              </div>
              <div className="privacy-subcard">
                <h4>b. Usage Data</h4>
                <p>IP address, browser type, interaction metrics, and system logs.</p>
              </div>
              <div className="privacy-subcard privacy-grid-full">
                <h4>c. Web3 Specific Data</h4>
                <p>Public wallet addresses and transaction hashes explicitly shared with us. <span className="text-blue">We never ask for private keys.</span></p>
              </div>
            </div>
          </section>

          {/* Section 2 */}
          <section id="use" className="privacy-section-card">
            <div className="privacy-section-header">
              <div className={`privacy-icon-wrapper ${sections[1].iconClass}`}>
                {sections[1].icon}
              </div>
              <h2 className="privacy-section-title">2. Use of Information</h2>
            </div>
            <p className="privacy-text">
              We utilize the collected information strictly to enhance your experience and secure our ecosystem. Purposes include:
            </p>
            <ul className="privacy-list">
              {['Providing, operating, and maintaining our services.', 'Improving and personalizing user experiences.', 'Understanding how users interact with our platform.', 'Communicating updates, security alerts, and support.'].map((item, i) => (
                <li key={i}>
                  <FaRegCheckCircle className="privacy-list-icon" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* Section 3 */}
          <section id="share" className="privacy-section-card">
            <div className="privacy-section-header">
              <div className={`privacy-icon-wrapper ${sections[2].iconClass}`}>
                {sections[2].icon}
              </div>
              <h2 className="privacy-section-title">3. Sharing of Information</h2>
            </div>
            <p className="privacy-text">
              We do not sell your personal data. We only share information with third parties in the following scenarios:
            </p>
            <div>
              <div className="privacy-row">
                <FaLock className="privacy-row-icon" />
                <div>
                  <h4>Service Providers</h4>
                  <p>Trusted partners who assist us in operating our platform, under strict confidentiality agreements.</p>
                </div>
              </div>
              <div className="privacy-row">
                <FaGlobe className="privacy-row-icon" />
                <div>
                  <h4>Legal Compliance</h4>
                  <p>If required by law, regulation, or regulatory authorities.</p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 4 - Special Warning Section */}
          <section id="disclaimer" className="privacy-disclaimer">
            <div className="privacy-disclaimer-bg" />
            <div className="privacy-disclaimer-content">
              <div className="privacy-section-header">
                <div className={`privacy-icon-wrapper ${sections[3].iconClass}`}>
                  {sections[3].icon}
                </div>
                <h2 className="privacy-section-title">4. Blockchain & Crypto Disclaimer</h2>
              </div>
              <div className="privacy-disclaimer-box">
                <p>
                  By nature, <strong>blockchain transactions are public, irreversible, and permanent</strong>. When you interact with our decentralized protocols, the associated data (like wallet addresses and transaction hashes) are recorded on public ledgers out of our control.
                </p>
                <p>
                  <span className="text-yellow">Please be aware:</span> We cannot modify, delete, or obscure information that is published on a blockchain network. Use decentralized services at your own discretion and always safeguard your private keys.
                </p>
              </div>
            </div>
          </section>

          {/* Section 5 */}
          <section id="cookies" className="privacy-section-card">
            <div className="privacy-section-header">
              <div className={`privacy-icon-wrapper ${sections[4].iconClass}`}>
                {sections[4].icon}
              </div>
              <h2 className="privacy-section-title">5. Cookies and Tracking</h2>
            </div>
            <p className="privacy-text">
              We use cookies and similar tracking technologies to track activity on our service and hold certain information. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some portions of our service.
            </p>
          </section>

          {/* Section 6 */}
          <section id="security" className="privacy-section-card">
            <div className="privacy-section-header">
              <div className={`privacy-icon-wrapper ${sections[5].iconClass}`}>
                {sections[5].icon}
              </div>
              <h2 className="privacy-section-title">6. Data Security</h2>
            </div>
            <p className="privacy-text">
              The security of your data is paramount. We implement industry-standard encryption, firewalls, and access control measures to ensure your information is protected. However, no method of transmission over the Internet, or method of electronic storage is 100% secure.
            </p>
          </section>

          {/* Section 7 */}
          <section id="retention" className="privacy-section-card">
            <div className="privacy-section-header">
              <div className={`privacy-icon-wrapper ${sections[6].iconClass}`}>
                {sections[6].icon}
              </div>
              <h2 className="privacy-section-title">7. Data Retention</h2>
            </div>
            <p className="privacy-text">
              We will retain your Personal Data only for as long as is necessary for the purposes set out in this Privacy Policy. We will retain and use your Personal Data to the extent necessary to comply with our legal obligations.
            </p>
          </section>

          {/* Section 8 - Special Checklist */}
          <section id="rights" className="privacy-section-card">
            <div className="privacy-section-header">
              <div className={`privacy-icon-wrapper ${sections[7].iconClass}`}>
                {sections[7].icon}
              </div>
              <h2 className="privacy-section-title">8. Your Rights</h2>
            </div>
            <p className="privacy-text">
              We believe you should have complete control over your data. You possess the following rights regarding your personal information:
            </p>
            <div className="privacy-grid">
              {[
                { title: "Right to Access", desc: "Request copies of your personal data." },
                { title: "Right to Rectification", desc: "Request correction of inaccurate data." },
                { title: "Right to Erasure", desc: "Request deletion of your personal data." },
                { title: "Right to Restrict", desc: "Request restricting processing of your data." }
              ].map((right, idx) => (
                <div key={idx} className="privacy-rights-card">
                  <div className="privacy-rights-icon">
                    <FaRegCheckCircle />
                  </div>
                  <div>
                    <h4>{right.title}</h4>
                    <p>{right.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Section 9 */}
          <section id="international" className="privacy-section-card">
            <div className="privacy-section-header">
              <div className={`privacy-icon-wrapper ${sections[8].iconClass}`}>
                {sections[8].icon}
              </div>
              <h2 className="privacy-section-title">9. International Transfers</h2>
            </div>
            <p className="privacy-text">
              Your information, including Personal Data, may be transferred to — and maintained on — computers located outside of your state, province, country, or other governmental jurisdiction where the data protection laws may differ than those from your jurisdiction.
            </p>
          </section>

          {/* Section 10 */}
          <section id="changes" className="privacy-section-card">
            <div className="privacy-section-header">
              <div className={`privacy-icon-wrapper ${sections[9].iconClass}`}>
                {sections[9].icon}
              </div>
              <h2 className="privacy-section-title">10. Changes to This Policy</h2>
            </div>
            <p className="privacy-text">
              We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Effective Date" at the top. We advise you to review this Privacy Policy periodically.
            </p>
          </section>

          {/* Section 11 - CTA Style */}
          <section id="contact" className="privacy-cta">
            <div className="privacy-cta-pattern" />
            <div className="privacy-cta-content">
              <div className="privacy-cta-icon">
                <FaEnvelope />
              </div>
              <h2 className="privacy-cta-title">11. Contact Us</h2>
              <p className="privacy-cta-text">
                Have questions about this Privacy Policy or how we handle your data? Our dedicated privacy team is ready to help.
              </p>
              <div className="privacy-cta-buttons">
                <a href="mailto:privacy@xeertechnology.com" className="privacy-btn-primary">
                  <span>Contact Privacy Team</span>
                  <FaArrowRight size={14} />
                </a>
                <a href="/contact-us" className="privacy-btn-secondary">
                  General Inquiries
                </a>
              </div>
            </div>
          </section>

        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
